/**
 * Stale-chunk auto-recovery.
 *
 * Background: every Vite build hashes file names (Dashboard-XYZ.js).
 * When we deploy, a user with the app already open is holding the old
 * hash references in memory. The moment React.lazy() tries to load a
 * chunk that no longer exists on the server, Vite throws an error.
 *
 * Before this module:
 *   - ErrorBoundary caught some chunk errors (matched on message), but
 *     only after the error propagated up through the React tree.
 *   - Many chunk errors fire OUTSIDE the React tree — module preloads,
 *     Suspense boundaries that resolve to "module not found." Those
 *     hit window.error / window.unhandledrejection and bypass the
 *     boundary entirely. Users saw "Something went wrong" (the generic
 *     fallback) with a real error message about a missing JS file —
 *     refresh fixed it because the new HTML pointed at the new hashes.
 *
 * After this module:
 *   - vite:preloadError listener auto-reloads on the most common path
 *     (a Suspense boundary trying to preload a stale chunk).
 *   - window.error / unhandledrejection filters catch dynamic-import
 *     failures that slip past vite:preloadError.
 *   - Both paths set a sessionStorage flag so we never loop-reload
 *     (if reload itself fails to fix the issue, fall through to the
 *     ErrorBoundary so the user sees something instead of an
 *     infinite refresh).
 *
 * The ErrorBoundary's existing in-render chunk detection stays as a
 * belt-and-suspenders. Combined coverage should bring the "oops" rate
 * after deploys close to zero.
 */

const RECOVERY_KEY = "tp_chunk_recovery_attempted_at";
// 60s window — if a reload happens and the error reoccurs within this
// time, the page is likely broken for a real reason, not a stale
// chunk. We stop reloading and let the ErrorBoundary catch the issue
// so the user sees a real error message.
const RECOVERY_WINDOW_MS = 60_000;

/**
 * Was a recovery reload attempted very recently? If yes, the reload
 * didn't fix the underlying error and we should NOT loop. The
 * timestamp lives in sessionStorage so it resets on tab close.
 */
function recentlyAttemptedRecovery(): boolean {
  try {
    const raw = sessionStorage.getItem(RECOVERY_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < RECOVERY_WINDOW_MS;
  } catch {
    return false;
  }
}

function markRecoveryAttempted() {
  try {
    sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));
  } catch {
    // Best-effort; sessionStorage can throw in some sandboxed contexts.
  }
}

/**
 * Wipe service-worker caches before reloading. If a stale SW cached
 * the old index.html or the old chunk file paths, a plain reload
 * would re-load them. Clearing first ensures the next reload pulls
 * fresh from the server.
 *
 * Returns a promise that resolves when wipe is done (or immediately
 * if not supported / no caches present).
 */
async function clearCachesIfPossible(): Promise<void> {
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (err) {
    console.warn("[chunk-recovery] cache clear failed (non-fatal):", err);
  }
}

/**
 * Trigger the auto-reload. No-op if we've already tried in the past
 * RECOVERY_WINDOW_MS (to avoid a refresh loop on persistently broken
 * builds).
 */
async function attemptRecovery(reason: string): Promise<void> {
  if (recentlyAttemptedRecovery()) {
    console.warn(
      `[chunk-recovery] skipping reload (${reason}) — already attempted in the last ${RECOVERY_WINDOW_MS}ms`,
    );
    return;
  }
  console.warn(`[chunk-recovery] reloading due to ${reason}`);
  markRecoveryAttempted();
  await clearCachesIfPossible();
  window.location.reload();
}

/**
 * Pattern-match a thrown error message to decide if it's a stale-chunk
 * symptom. Kept centralized so the ErrorBoundary can use the same
 * matcher (single source of truth — see ErrorBoundary.tsx wiring).
 */
export function isChunkLoadErrorMessage(message: string | undefined | null): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return (
    m.includes("failed to fetch dynamically imported module") ||
    m.includes("dynamically imported module") ||
    m.includes("loading chunk") ||
    m.includes("loading css chunk") ||
    m.includes("importing a module script failed") ||
    m.includes("error loading dynamically imported module") ||
    // Vite + Safari sometimes surfaces this generic message when a
    // preloaded .js 404s. Risky to match too broad ("Failed to fetch"
    // could be a legit API error), so we anchor on .js/.css URLs.
    /failed to fetch.*\.(js|css|mjs)/.test(m)
  );
}

/**
 * Install global listeners. Call ONCE at app boot (main.tsx).
 * Idempotent — calling twice does nothing the second time.
 */
let installed = false;
export function installChunkRecovery(): void {
  if (installed) return;
  installed = true;

  // Vite's own signal — fired when an <link rel="modulepreload"> for
  // a code-split chunk fails. The most reliable indicator of a stale
  // deploy mid-session.
  window.addEventListener("vite:preloadError", (event: Event) => {
    event.preventDefault(); // stop Vite's default of re-throwing
    attemptRecovery("vite:preloadError");
  });

  // Catch dynamic import errors that surface as unhandled promise
  // rejections (most common path when a Suspense boundary's lazy
  // import 404s mid-navigation).
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      typeof reason === "string"
        ? reason
        : reason instanceof Error
          ? reason.message
          : (reason && typeof reason === "object" && "message" in reason
              ? String((reason as { message: unknown }).message)
              : "");
    if (isChunkLoadErrorMessage(message)) {
      event.preventDefault();
      attemptRecovery("unhandledrejection");
    }
  });

  // Catch synchronous errors from <script> tags failing to load —
  // rare for code-split chunks but possible if the bundler emits a
  // <link rel="modulepreload"> the browser tries to evaluate.
  window.addEventListener(
    "error",
    (event) => {
      // event.target is the failing element (Script/Link), not the
      // window, on resource errors. event.message is empty in that
      // case; use the target's src/href to detect.
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "SCRIPT" || target.tagName === "LINK")) {
        const src = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href;
        if (src && /\.(js|css|mjs)(\?|$)/.test(src)) {
          attemptRecovery(`resource error: ${src}`);
          return;
        }
      }
      // Fall back to message-matching for script errors that bubble
      // to window.error.
      if (isChunkLoadErrorMessage(event.message)) {
        attemptRecovery("window error event");
      }
    },
    true, // useCapture — needed for resource errors which don't bubble
  );
}
